import { RecordButton } from '@/components/desktop/RecordButton'
import { TooltipShortcutHint } from '@/components/layout/KeyboardShortcutsDialog'
import { PendingWritesIndicator } from '@/components/layout/PendingWritesIndicator'
import { ModeToggle } from '@/components/mode-toggle'
import { TimeEntryWidget } from '@/components/time-entry'
import { useSidebar } from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import { useBennyStore } from '@/store/benny.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'

import type { AppPreferences } from '@scrambl/shared'

import { CustomSidebarTrigger } from './AppSidebar'
import { NotificationButton } from './NotificationButton'
import { OmniSearch } from './OmniSearch'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { UserMenu } from './UserMenu'

interface HeaderProps {
  onOpenChange?: (open: boolean) => void
}

export function Header({ onOpenChange }: HeaderProps) {
  const isMobile = useIsMobile()
  const { state } = useSidebar()
  // With Simple Mode on, Benny lives in the sidebar ("Ask Benny") — the
  // header face would be a second, competing entry point.
  const { checkFeatureFlag } = useFeatureFlags(['SIMPLE_MODE'])
  const bennyInSidebar = checkFeatureFlag('SIMPLE_MODE')
  const isSidebarCollapsed = state === 'collapsed'
  const toggleBenny = useBennyStore(s => s.toggle)
  const storedPreferences = useUserPreferencesStore(s => s.preferences)
  const prefsInitialized = useUserPreferencesStore(s => s.isInitialized)
  const bennyAppPref = getUserAppPreference(storedPreferences)
  const bennyEnabled =
    prefsInitialized &&
    (bennyAppPref?.preferences as AppPreferences | undefined)?.bennyEnabled ===
      true &&
    !bennyInSidebar

  const isDesktopApp = isTauri() && !isMobile

  // For Tauri desktop app, the header is rendered at the MainLayout level
  // So we don't render anything here
  if (isDesktopApp) {
    return null
  }

  // Regular web/mobile header - white in light mode, black in dark mode
  return (
    <header
      className={cn(
        'sticky top-0 z-30 w-full transition-colors duration-300',
        'bg-white dark:bg-[hsl(var(--cast-iron-skillet))]',
        'border-b border-border/50',
      )}
    >
      <div
        className={cn(
          'flex w-full flex-wrap items-center gap-y-2 px-4 py-2 sm:h-14 sm:flex-nowrap sm:py-0',
        )}
      >
        {/* Left Side - Navigation & Time Entry */}
        <div className="order-1 flex flex-shrink-0 items-center gap-4">
          <div className="flex transition-transform duration-200 ease-out">
            <CustomSidebarTrigger className="flex" />
          </div>

          {/* Time Entry Widget + Meeting Recorder */}
          <div className="hidden items-center gap-2 sm:flex">
            <TimeEntryWidget />
            <RecordButton />
          </div>
        </div>

        {/* Center - OmniSearch + Benny */}
        <div className="order-3 flex w-full min-w-0 flex-1 items-center justify-center sm:order-2 sm:px-2">
          <div className="flex w-full items-center gap-2 sm:max-w-xl">
            <OmniSearch className="w-full" />
            {bennyEnabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleBenny}
                    className={cn(
                      'h-8 w-8 flex-shrink-0 overflow-hidden rounded-full',
                      'bg-purple-hen',
                      'transition-all duration-200 hover:scale-105 active:scale-95',
                      'ring-purple-hen/20 ring-1',
                    )}
                    style={{
                      backgroundImage: 'url(/characters/Default.svg)',
                      backgroundSize: '140%',
                      backgroundPosition: 'center 10%',
                      backgroundRepeat: 'no-repeat',
                    }}
                    aria-label="Ask Benny"
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  Ask Benny
                  <TooltipShortcutHint keys={['Mod', '.']} />
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Right Side - Organization & User */}
        <div className="order-2 flex flex-shrink-0 items-center gap-2 sm:order-3">
          {/* Utility Actions */}
          <div className="flex items-center gap-1">
            <ModeToggle />
            <NotificationButton />
          </div>
          {/* Renders only when a write is queued or the connection is down. */}
          <PendingWritesIndicator />
          {/* Organization Switcher */}
          <div
            className={cn(
              'hidden transition-opacity duration-200 sm:block',
              isSidebarCollapsed && !isMobile ? 'w-auto' : 'max-w-[200px]',
            )}
          >
            <OrganizationSwitcher className={isMobile ? 'w-full' : undefined} />
          </div>
          <div>
            <UserMenu
              onOpenChange={onOpenChange}
              getNavigationPath={path => path}
            />
          </div>
        </div>
      </div>
    </header>
  )
}
